// dependencies
import React from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

// fontawesome
import {
	faArrowLeft,
	faArrowUpFromSquare,
	faBallot,
	faBell,
	faBriefcaseBlank,
	faCalendarDays,
	faChartNetwork,
	faChartPieSimple,
	faChartTreeMap,
	faCompass,
	faFolder,
	faFolderArrowUp,
	faGear,
	faHouse,
	faLightbulb,
	faMessageLines,
	faMessages,
	faMicrochipAi,
	faPenField,
	faPollPeople,
	faRankingStar,
	faShieldPlus,
	faTableList,
	faUser,
	faUsers,
	faWallet,
} from "@performyard/fontawesome/pro-regular-svg-icons";
import { faHeartPulse } from "@performyard/fontawesome/pro-solid-svg-icons";

// components
import VerticalNavBar from "../../components/VerticalNavBar";
import { NavItem } from "../../components/VerticalNavBar/SidebarItem";

import { useCurrentRoute } from "../../app/router";

import {
	useCompanyFields,
	useCurrentUser,
	useRbacPrimaryRolePermissions,
	useVideoConferencingProviderFlags,
} from "../../api/hooks";
//hooks
import { useFeatureFlag, useFeatureFlagQuery, useRoleFieldEnabled } from "../../hooks/useFeatureFlag";

// selectors
import { getIsUserSuperuser, getUserPermissionType } from "../../selectors/app";
import { getCompanySettingsContext, getCustomBilling } from "../../selectors/settings";

import RouterTags from "../../constants/RouterTags";

const AdminVerticalNavBar = () => {
	const rootElement = document.getElementById("admin-nav-bar");
	const companyID = rootElement?.getAttribute("company") || "";

	// user company custom settings
	const isUserSuperuser = useSelector(getIsUserSuperuser);
	const companySettings = useSelector(getCompanySettingsContext);
	const isBillingAdmin = useSelector(getUserPermissionType) === "billing-admins";
	const hasCustomBilling = useSelector(getCustomBilling);

	// get feature enabled settings
	const { value: groupsFeatureVisible } = useFeatureFlagQuery("ff-is-groups-enabled", false);
	const groupsEnabled = companySettings.getIn(["groups", "state"], "disabled") === "enabled" && groupsFeatureVisible;

	const { value: conversationalAIFeatureVisible } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);

	const reviewsFeatureVisible = useFeatureFlag("ff-is-reviews-enabled", true);
	const reviewsEnabled = companySettings.getIn(["reviews", "state"], "disabled") === "enabled";

	const { value: meetingsEnabled } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	const { value: goalsFeatureVisible } = useFeatureFlagQuery("ff-is-goals-enabled", false);
	const goalsEnabled = companySettings.getIn(["goals", "state"], "disabled") === "enabled";

	const { value: feedbackFeatureVisible } = useFeatureFlagQuery("ff-is-feedback-enabled", true);
	const feedbackEnabled =
		companySettings.getIn(["feedback", "state"], "disabled") === "enabled" && feedbackFeatureVisible;

	const { value: engagementSurveyEnabled } = useFeatureFlagQuery("ff-is-engagement-survey-enabled", false);
	const workflowsFeatureVisible = useFeatureFlag("ff-is-workflows-enabled", true);
	const workflowsEnabled = companySettings.getIn(["workflows", "state"], "disabled") === "enabled";
	const { value: docSigningFeatureVisible } = useFeatureFlagQuery("ff-is-document-signing-enabled", false);
	const docSigningEnabled =
		companySettings.getIn(["documentSigning", "state"], "disabled") === "enabled" && docSigningFeatureVisible;
	const integrationsEnabled = companySettings.getIn(["hrIntegration", "state"], "disabled") !== "disabled";
	const { value: sftpFeatureVisible } = useFeatureFlagQuery("ff-is-enabled-sftp-integration", false);
	const sftpEnabled =
		companySettings.getIn(["integrationSftp", "state"], "disabled") === "enabled" && sftpFeatureVisible;
	const { value: calendarIntegrationFeatureVisible } = useFeatureFlagQuery(
		"ff-is-calendar-integration-enabled",
		false
	);
	const { value: meetingsIntegrationsFeatureVisible } = useFeatureFlagQuery(
		"ff-is-meetings-integrations-enabled",
		false
	);
	const { anyEnabled: anyVideoProviderEnabled } = useVideoConferencingProviderFlags();
	const meetingsIntegrationsNavVisible =
		calendarIntegrationFeatureVisible || (meetingsIntegrationsFeatureVisible && anyVideoProviderEnabled);
	const { value: compensationEnabled } = useFeatureFlagQuery("ff-compensation", false);

	// SCIM
	const { value: scimFeatureVisible } = useFeatureFlagQuery("ff-is-scim-enabled", false);
	const scimEnabled = companySettings.getIn(["scim_api", "state"], "disabled") === "enabled" && scimFeatureVisible;

	// ai feature flag enabled
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	const { value: employeeInsightsEnabled } = useFeatureFlagQuery("ff-temp-is-ai-employee-insights-enabled", false);

	// coaching
	const { value: coachingEnabled } = useFeatureFlagQuery("ff-is-coaching-enabled", false);
	const coachingNavVisible = aiFeatureFlagEnabled && coachingEnabled;

	const { value: surveysEnabled } = useFeatureFlagQuery("ff-is-surveys-enabled", false);
	const { value: administrativeDocumentsVisible } = useFeatureFlagQuery("ff-temp-administrative-documents", false);
	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", false);
	const { value: competenciesEnabled } = useFeatureFlagQuery("ff-is-competency-management-enabled", false);
	const { value: roleFieldEnabled } = useRoleFieldEnabled();

	const companyLevelHiddenItems = {
		documents: companySettings ? companySettings.getIn(["documents", "state"], "enabled") === "hidden" : false,
		notifications: companySettings
			? companySettings.getIn(["notifications", "state"], "enabled") === "hidden"
			: false,
	};

	const hiddenItems = {
		...companyLevelHiddenItems,
		documents: !isBillingAdmin ? true : companyLevelHiddenItems.documents,
	};

	const currentRoute = useCurrentRoute();

	// Check if current path is an administration or super-user-admin route
	const pathname = window.location.pathname;
	const targetCompanyID = new URLSearchParams(window.location.search).get("companyId");
	const isAdminRoute = pathname.startsWith("/administration") || pathname.startsWith("/super-user-admin");

	// Show nav if route has SHOW_ADMIN_NAV tag OR if on an admin route.
	// HIDE_ADMIN_NAV wins over both so full-focus routes (e.g. competency create/edit)
	// can suppress the sidebar even though they live under /administration.
	const routeTags = currentRoute?.route?.handle?.tags;
	const hideAdminNav = routeTags?.includes(RouterTags.HIDE_ADMIN_NAV);
	const shouldShowNav = !hideAdminNav && (isAdminRoute || routeTags?.includes(RouterTags.SHOW_ADMIN_NAV));

	const { data: engagementPermissions, isLoading: isEngagementPermissionsLoading } =
		useRbacPrimaryRolePermissions("engagement");
	const { data: compensationPermissions, isLoading: isCompensationPermissionsLoading } =
		useRbacPrimaryRolePermissions("compensation");
	const userCanViewCompensation =
		!isCompensationPermissionsLoading && compensationPermissions?.includes("compensation:viewSettings");

	const userCanEditEngagement =
		!isEngagementPermissionsLoading && engagementPermissions?.includes("engagement:editSettings");

	const { data: scimPermissions, isLoading: isScimPermissionsLoading } = useRbacPrimaryRolePermissions("scim_api");
	const userCanEditScim = !isScimPermissionsLoading && scimPermissions?.includes("scim_api:viewScimApiToken");

	// Roles admin is gated behind the Competency FF and RBAC.
	const { data: rolesPermissions, isLoading: isRolesPermissionsLoading } = useRbacPrimaryRolePermissions("roles");
	const userCanViewRoles = !isRolesPermissionsLoading && rolesPermissions?.includes("roles:viewAdmin");

	// FF gates the module; hasRoleField guards the pre-migration window (no field yet).
	// On a fields-query error the guard fails open: it is a secondary check, and treating an
	// error as "no role field" would permanently hide the nav item for entitled companies.
	// The FF gate itself stays fail-closed (fallback false), matching the backend gates.
	const { data: currentUser } = useCurrentUser();
	const companyId = currentUser?.me?.authorization?.payload?.company_id;
	const { data: companyFields, isError: isFieldsError } = useCompanyFields(companyId || "");
	const hasRoleField = isFieldsError || !!companyFields?.some((field) => field.type === "role");

	// function to get path based on if user is super user
	const getPath = (path: string, hasSuperUserPath: boolean) => {
		if (isUserSuperuser && hasSuperUserPath) {
			if (path === "administration") {
				if (targetCompanyID) {
					return `/administration/?companyId=${encodeURIComponent(targetCompanyID)}`;
				}
				return pathname.startsWith("/super-user-admin")
					? `/administration/?companyId=${encodeURIComponent(companyID)}`
					: "/administration/";
			}
			if (path === "signature-requests") {
				return "/super-user-admin/signature-requests/";
			}
			if (path === "workflows") {
				return "/super-user-admin/workflows/";
			}
			return `/super-user-admin/${path}/${companyID}/`;
		} else {
			if (path === "administration") {
				return "/administration/";
			}
			if (path === "integrations") {
				return "/integrations/";
			}
			return `/administration/${path}/`;
		}
	};

	const AIChildren = [
		...(aiFeatureFlagEnabled && reviewsEnabled
			? [
					{
						title: "AI Rephrase",
						path: getPath("ai-assistants/ai-rephrase", false),
					},
				]
			: []),
		...(aiFeatureFlagEnabled &&
		(reviewsEnabled || engagementSurveyEnabled || meetingsEnabled || surveysEnabled || employeeInsightsEnabled)
			? [
					{
						title: "AI Insights",
						path: getPath("ai-assistants/ai-insights", false),
					},
				]
			: []),
	];

	const workflowsChildren = [
		workflowsFeatureVisible && workflowsEnabled
			? { title: "Workflow Templates", path: getPath("workflows", true) }
			: null,
		docSigningEnabled ? { title: "Signature Requests", path: getPath("signature-requests", true) } : null,
	].filter((item) => item !== null) as NavItem[];

	const navItems = [
		{
			// The app-wide side nav is suppressed on admin pages, so this is the way back to the product.
			title: "My Dashboard",
			icon: faArrowLeft,
			path: "/",
			ariaLabel: "Back to my dashboard",
		},
		{
			title: "Admin Home",
			icon: faHouse,
			path: getPath("administration", true),
			divider: "before" as const,
		},
		{
			title: "Employees",
			icon: faUser,
			path: getPath("employees", true),
		},
		{
			title: "Employee Data Fields",
			icon: faTableList,
			path: getPath("data-fields", true),
		},
		{
			title: "Employee Data Changes",
			icon: faBallot,
			path: getPath("field-changes", false),
		},
		groupsEnabled
			? {
					title: "Groups",
					icon: faUsers,
					path: getPath("groups", true),
				}
			: null,
		reviewsFeatureVisible && (reviewsEnabled || isUserSuperuser)
			? {
					title: "Reviews",
					icon: faPenField,
					children: [
						{ title: "Forms", path: getPath("reviews/forms", true) },
						{ title: "Templates", path: getPath("reviews/templates", true) },
						{ title: "Graph Settings", path: getPath("question-maps", false) },
					],
				}
			: null,
		goalsFeatureVisible && (goalsEnabled || isUserSuperuser)
			? { title: "Goals", icon: faChartPieSimple, path: getPath("goals", true) }
			: null,
		compensationEnabled && userCanViewCompensation
			? { title: "Compensation", icon: faRankingStar, path: getPath("compensation", false) }
			: null,
		feedbackEnabled || isUserSuperuser
			? { title: "Feedback", icon: faMessageLines, path: getPath("feedback", true) }
			: null,
		aiFeatureFlagEnabled && conversationalAIFeatureVisible
			? { title: "Ask PerformYard", icon: faMessages, path: getPath("conversational-ai", false) }
			: null,
		// AIChildren will be an empty array if the ai feature flag is not enabled, no need to check flag here.
		AIChildren.length > 0 ? { title: "AI Assistants", icon: faMicrochipAi, children: AIChildren } : null,
		coachingNavVisible ? { title: "Coaching", icon: faCompass, path: getPath("coaching", false) } : null,
		(engagementSurveyEnabled && userCanEditEngagement) || isUserSuperuser
			? {
					title: "Engagement",
					icon: faHeartPulse,
					children: [
						{ title: "Pulse Survey Settings", path: getPath("engagement", false) },
						{ title: "Employee Cohorts", path: getPath("engagement/cohorts", false) },
						{ title: "Excluded Employees", path: getPath("engagement/excluded-employees", false) },
						{
							title: customFactorsEnabled ? "Questions & Factors" : "Questions",
							path: getPath("engagement/questions", false),
						},
					],
				}
			: null,
		roleFieldEnabled && userCanViewRoles && hasRoleField
			? { title: "Roles", icon: faBriefcaseBlank, path: getPath("roles", false) }
			: null,
		competenciesEnabled ? { title: "Competencies", icon: faLightbulb, path: getPath("competencies", false) } : null,
		workflowsChildren.length > 0
			? {
					title: "Workflows",
					icon: faChartTreeMap,
					children: workflowsChildren,
				}
			: null,
		meetingsIntegrationsNavVisible
			? { title: "Meetings Integrations", icon: faCalendarDays, path: getPath("meetings-integrations", false) }
			: null,
		integrationsEnabled
			? { title: "HRIS Integration", icon: faArrowUpFromSquare, path: getPath("integrations", false) }
			: null,
		sftpEnabled ? { title: "SFTP Setup", icon: faFolderArrowUp, path: getPath("sftp", false) } : null,
		userCanEditScim && scimEnabled
			? { title: "SCIM Setup", icon: faChartNetwork, path: getPath("scim", false) }
			: null,
		administrativeDocumentsVisible && (!hiddenItems.documents || isUserSuperuser)
			? { title: "Documents", icon: faFolder, path: getPath("documents", true) }
			: null,
		!hiddenItems.notifications || isUserSuperuser
			? { title: "Notifications", icon: faBell, path: getPath("notifications", false) }
			: null,
		(isBillingAdmin || isUserSuperuser) && !hasCustomBilling
			? { title: "Billing", icon: faWallet, path: getPath("billing", true) }
			: null,
		{ title: "Authentication", icon: faShieldPlus, path: getPath("authentication-settings", false) },
		isUserSuperuser ? { title: "Company Setup", icon: faGear, path: getPath("setup", true) } : null,
		{ title: "Bulk User Logs", icon: faPollPeople, path: getPath("sync-log", false) },
	].filter((item: NavItem | null) => item !== null) as NavItem[];

	const content = shouldShowNav ? <VerticalNavBar navItems={navItems} /> : null;

	if (rootElement) {
		return ReactDOM.createPortal(content, rootElement);
	}
};

export default AdminVerticalNavBar;
